import { Check, ChevronsUpDown, Users, Building2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useTenant } from "@/hooks/useTenant";
import { useState } from "react";

export function FamilySwitcher() {
  const { families, familyId, setFamilyId, isMFO, isClientView, isLoading } = useTenant();
  const [open, setOpen] = useState(false);

  if (!isMFO || isLoading) return null;

  const selectedFamily = families?.find((f) => f.id === familyId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={isClientView ? "secondary" : "outline"}
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-[200px] justify-between",
            isClientView && "bg-amber-100 hover:bg-amber-200 border-amber-300 dark:bg-amber-900/50 dark:hover:bg-amber-900/70 dark:border-amber-700"
          )}
        >
          {isClientView ? (
            <Eye className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
          ) : (
            <Building2 className="h-4 w-4 shrink-0" />
          )}
          <span className="truncate">
            {selectedFamily?.name || "Firm View"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0 bg-popover" align="start">
        <Command>
          <CommandInput placeholder="Search families..." />
          <CommandList>
            <CommandEmpty>No family found.</CommandEmpty>
            <CommandGroup heading="View Mode">
              <CommandItem
                value="firm-view"
                onSelect={() => {
                  setFamilyId(null);
                  setOpen(false);
                }}
                className={cn(!familyId && "bg-accent")}
              >
                <Building2 className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="font-medium">Firm View</span>
                  <span className="text-xs text-muted-foreground">All families aggregated</span>
                </div>
                <Check
                  className={cn(
                    "ml-auto h-4 w-4",
                    !familyId ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="View as Client">
              {families?.map((family) => (
                <CommandItem
                  key={family.id}
                  value={family.name}
                  onSelect={() => {
                    setFamilyId(family.id);
                    setOpen(false);
                  }}
                  className={cn(familyId === family.id && "bg-amber-50 dark:bg-amber-900/30")}
                >
                  <Users className="h-4 w-4" />
                  <span className="flex-1">{family.name}</span>
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      familyId === family.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
